import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { LandSection, AnalysisSectionHeader } from '@popety-io/ui'
import type { Land } from '@popety-io/types'
import { useClearMapSection } from '../hooks'

const LandDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border.default};
  margin: ${({ theme }) => theme.spacing.large} 0;
`

interface LandTabProps {
  estimateData: any
}

export const LandTab: React.FC<LandTabProps> = ({ estimateData }) => {
  const { t } = useTranslation('landAnalysis')

  // Clear map section data when viewing land tab
  useClearMapSection()

  // Match main app: use value_edited.lands (pre-fetched full land data) or value.lands
  const lands: Land[] =
    estimateData?.value_edited?.lands || estimateData?.value?.lands || []

  // Get address from estimate details
  const address = estimateData?.value?.address_full || ''

  // Generate subtitle based on number of lands
  const getSubtitle = () => {
    if (!address) return t('header.subtitle')
    const landCount = lands.length
    if (landCount === 0) return t('header.subtitleWithAddress', { address })
    if (landCount === 1) return t('header.subtitleSinglePlot', { address })
    return t('header.subtitleMultiplePlots', { count: landCount, address })
  }

  // Loading state
  if (!estimateData) {
    return (
      <>
        <AnalysisSectionHeader title={t('header.title')} subtitle="" />
        <LandSection land={null} loading={true} t={t} />
      </>
    )
  }

  // Empty state
  if (lands.length === 0) {
    return (
      <>
        <AnalysisSectionHeader
          title={t('header.title')}
          subtitle={getSubtitle()}
        />
        <LandSection land={null} t={t} />
      </>
    )
  }

  // Display all lands with dividers between them (read-only, no retry)
  return (
    <>
      <AnalysisSectionHeader
        title={t('header.title')}
        subtitle={getSubtitle()}
      />
      {lands.map((land, index) => (
        <React.Fragment key={land.id || land.land_id || index}>
          {index > 0 && <LandDivider />}
          <LandSection land={land} t={t} />
        </React.Fragment>
      ))}
    </>
  )
}
